import type { TFunction } from "i18next";
import type { CollectionVersionSummary } from "../../../api/content/contentDecoders";
import type { VersionLabel } from "../../../api/content/versionDecoders";
import { formatDateTime } from "../../../shared/formatting/dateTimes";
import { shortReference } from "../../../shared/references/shortReferences";
import { messageSubject } from "../../ontology/modelVersions";
import { versionOptionText } from "./versionText";

/**
 * A content or corpus version in a list of choices: by its label, or, before it has one, by
 * its change's subject (its short commit without one) and date. Moved here from the Thread
 * views' own helper (`threads/data/versionLabels.ts`), so one owner names versions.
 */
export function versionChoiceText(t: TFunction, name: string | null, label: VersionLabel | null, version: CollectionVersionSummary, tags: readonly string[], language: string | undefined): string {
  if (label !== null) return versionOptionText(t, name, label, tags, language);
  const subject = messageSubject(version.message);
  return [...tags, subject === "" ? shortReference("gitObject", version.commit) : subject, formatDateTime(version.committedAt, language)].join(" · ");
}

/**
 * A model version on the Data model page (F77): its release number and date, or its date alone
 * before it has a label; never its commit message, which names the change, not the version.
 */
export function modelVersionText(label: VersionLabel | null, version: CollectionVersionSummary, tags: readonly string[], language: string | undefined): string {
  return [...tags, ...(label === null ? [] : [label.version]), formatDateTime(version.committedAt, language)].join(" · ");
}

/** The shortest name of a model version, as in "Checks of 1.3.0": its release number, else its date. */
export function modelVersionName(label: VersionLabel | null, version: CollectionVersionSummary, language: string | undefined): string {
  return label?.version ?? formatDateTime(version.committedAt, language);
}
